import { trpc } from "../../../modules/main/components/trpc.ts";
import { useDebounce } from "../../../hooks/useDebounce";
import type { CCAMSearchFilters } from "cadendar-shared";
import { useMemo } from "react";

/**
 * Hook personnalisé pour la recherche CCAM avec debounce et pagination
 * Story 2.4: Intégration tRPC avec debounce 300ms et infinite scroll
 */
export const useCCAMSearch = (query: string, filters?: CCAMSearchFilters) => {
  const debouncedQuery = useDebounce(query, 300);

  const infiniteQuery = trpc.ccam.search.useInfiniteQuery(
    {
      query: debouncedQuery,
      filters,
      limit: 50,
    },
    {
      getNextPageParam: (lastPage) => {
        const { currentPage, totalPages } = lastPage.pagination;
        return currentPage < totalPages ? currentPage + 1 : undefined;
      },
      initialCursor: 1,
      enabled: debouncedQuery.length > 0,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 3,
    }
  );

  const flatData = useMemo(() => {
    return infiniteQuery.data?.pages.flatMap((page) => page.actes) || [];
  }, [infiniteQuery.data]);

  const hasMore = infiniteQuery.hasNextPage;

  return {
    ...infiniteQuery,
    data: { actes: flatData }, // Flattened data for UI consumption
    loadMore: infiniteQuery.fetchNextPage,
    hasMore: !!hasMore,
    resetPage: () => {
      // No-op: query key change (params) automatically resets the infinite query
    },
  };
};
